// Jedna lista nawigacji aplikacji — czyta ją panel 1440, dolny pasek 390, arkusz
// „Więcej” i szkielet trasy (rama bez danych). `key` to klucz i18n rozwiązywany
// przy renderze.
//
// MINIMAL-2026-09-18 (plan „Nawigacja w 4 pozycjach”, MAPA 30 stan 02): dwanaście
// pozycji w czterech grupach stało się CZTEREMA pozycjami bez ani jednego nagłówka
// grupy. Podstrony nie znikają — stają się zakładkami swojego ekranu (reguła 11
// języka minimal), więc ta sama lista niesie dziś dwie rzeczy: pozycje nawigacji
// i zakładki, na które każda z nich się rozwija.
//
// „Publikacja” renderuje się WYŁĄCZNIE, gdy sesyjna odpowiedź `GET /api/campaigns`
// mówi `enabled` (paczka 12 §H 2; `lib/autopilotAccess.ts`) — tylko ona zna listę
// beta. Konto bez dostępu ma trzy pozycje w panelu i cztery na dolnym pasku, BEZ
// podmiany pozycji (plan: „Żadnej podmiany pozycji”).
//
// Paczka L (reguła 43): „Opisy” mają JEDEN dom — Bibliotekę, w grupie PUBLIKACJA —
// dla każdego konta. Publikacja zostaje z czterema zakładkami. Adres `/captions`
// się nie zmienia, więc nie ma czego przekierowywać.

import type { GlyphName } from '../ds/media/Glyph'

export type NavItem = { to: string; key: string; glyph: GlyphName; end?: boolean }

/** Pozycja „Biblioteka” prowadzi na Muzykę — pierwszą ze swoich zakładek. */
export const LIBRARY_NAV: NavItem = { to: '/music', key: 'nav.sectionLibrary', glyph: 'klip' }
/** Pozycja „Publikacja” prowadzi na Planer — pierwszą ze swoich zakładek. */
export const PUBLISHING_NAV: NavItem = { to: '/autopilot', key: 'nav.sectionDistribution', glyph: 'kalendarz' }

/** Cztery pozycje w kolejności z artboardu 02. „Publikacja” tylko dla konta z dostępem. */
export const APP_NAV: NavItem[] = [
  { to: '/generator', key: 'nav.generator', glyph: 'edit' },
  { to: '/edits', key: 'nav.batches', glyph: 'seria' },
  LIBRARY_NAV,
  PUBLISHING_NAV,
]

export function appNav(access: boolean): NavItem[] {
  return APP_NAV.filter((it) => access || it !== PUBLISHING_NAV)
}

/** „Opisy”: czwarta zakładka Biblioteki, jedyna w grupie PUBLIKACJA. */
export const CAPTIONS_NAV: NavItem = { to: '/captions', key: 'nav.captions', glyph: 'opis' }

/** Grupy zakładek Biblioteki (reguła 43): co karmi Generator, a co Publikację. */
export type LibraryTabGroup = 'generator' | 'publishing'
export const LIBRARY_TAB_GROUPS: { id: LibraryTabGroup; key: string }[] = [
  { id: 'generator', key: 'nav.groupGenerator' },
  { id: 'publishing', key: 'nav.groupPublishing' },
]

/** Zakładki Biblioteki: GENERATOR: Muzyka · Klipy · Hooki / PUBLIKACJA: Opisy. */
export const LIBRARY_TABS: (NavItem & { group: LibraryTabGroup })[] = [
  { to: '/music', key: 'nav.music', glyph: 'utwor', group: 'generator' },
  { to: '/clip-packs', key: 'nav.clipPacks', glyph: 'klip', group: 'generator' },
  { to: '/hooks', key: 'nav.hooks', glyph: 'hook', group: 'generator' },
  { ...CAPTIONS_NAV, group: 'publishing' },
]

/**
 * Statystyki — CZWARTA zakładka Publikacji (sesja 36; piąta do paczki L, póki stały tam Opisy).
 *
 * Stoi OSOBNO, a nie w `PUBLISHING_TABS`, bo zakładka ma dwa warunki naraz: konto
 * z dostępem do publikacji ORAZ odpowiedź API mówiąca, że funkcja istnieje. Martwej
 * zakładki nie ma: na instalacji bez backendu statystyk pasek ma trzy pozycje.
 */
export const STATS_NAV: NavItem = { to: '/statystyki', key: 'nav.stats', glyph: 'staty' }

/** Zakładki Publikacji w kolejności pracy (wsad 22 §1): co · kiedy · na czym. Opisy mieszkają w Bibliotece. */
export const PUBLISHING_TABS: NavItem[] = [
  { to: '/autopilot', key: 'nav.campaigns', glyph: 'autopilot' },
  { to: '/kalendarz', key: 'nav.calendar', glyph: 'kalendarz' },
  { to: '/accounts', key: 'nav.accounts', glyph: 'konto' },
]

/** Zakładki Publikacji dla ekranu: czwarta wchodzi dopiero, gdy statystyki naprawdę są. */
export function publishingTabs(stats: boolean): NavItem[] {
  return stats ? [...PUBLISHING_TABS, STATS_NAV] : PUBLISHING_TABS
}

/** Trasy, które liczą się jako „Biblioteka” — z `/captions` dla każdego konta. */
export const LIBRARY_PATHS = LIBRARY_TABS.map((it) => it.to)
/** Trasy, które liczą się jako „Publikacja”. `/statystyki` też, choć zakładki bywa brak:
 *  konto, które na nią wejdzie, ma widzieć zapaloną pozycję nawigacji, nie żadnej. */
export const PUBLISHING_PATHS = [...PUBLISHING_TABS.map((it) => it.to), STATS_NAV.to]

/**
 * Do której POZYCJI nawigacji należy ta trasa: pozycja świeci dla wszystkich tras
 * swojej grupy, nie tylko dla tej jednej, na którą prowadzi (plan, zadanie B 1).
 * Zwraca `to` pozycji albo null dla tras spoza grup (Ustawienia, Gemy i plan).
 */
export function navGroupOf(pathname: string, access: boolean): string | null {
  const inGroup = (paths: string[]) => paths.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  if (inGroup(LIBRARY_PATHS)) return LIBRARY_NAV.to
  if (access && inGroup(PUBLISHING_PATHS)) return PUBLISHING_NAV.to
  return null
}

/** Konto: Gemy i plan (nie w buildzie natywnym), Ustawienia. Pomoc dokłada powłoka jako przycisk. */
export function accountNav(native: boolean): NavItem[] {
  return [
    ...(native ? [] : [{ to: '/billing', key: 'nav.billing', glyph: 'token' as GlyphName }]),
    { to: '/settings', key: 'nav.settings', glyph: 'ustawienia' },
  ]
}
